import { useEffect, useState } from 'react';
import { UZ_CITIES, districtsOf } from '@app/shared';
import { cityName } from '../domain/geo';
import { useI18n } from '../i18n/I18nProvider';
import { BottomSheet } from './BottomSheet';
import { Check, ChevronLeft, ChevronRight } from './icons';

/**
 * Выбор города и, где он есть, района — одной шторкой в два шага (ТЗ §14.3: модалка только
 * bottom sheet). Переиспользуется и в каталоге (фильтр клиента), и в профиле бизнеса.
 *
 * Два шага, а не два экрана: город без районного деления (все, кроме Ташкента) применяется
 * сразу по тапу и второго шага не показывает. Для Ташкента тап открывает список районов —
 * так шаг «район» не маячит там, где районов нет (см. `districtsOf`).
 *
 * Выбор возвращается ПАРОЙ `{ city, district }`, всегда согласованной: район приходит только
 * вместе со своим городом. Проверять связность на вызывающей стороне не нужно.
 */
export interface CitySelection {
  readonly city: string | null;
  readonly district: string | null;
}

export function CityPickerSheet(props: {
  open: boolean;
  value: CitySelection;
  /** Верхняя строка-сброс: «Все города» в каталоге, «Город не указан» в профиле. */
  clearLabel: string;
  onClose: () => void;
  onApply: (next: CitySelection) => void;
}): JSX.Element {
  const { t, lang } = useI18n();
  const [step, setStep] = useState<'city' | 'district'>('city');
  const [pendingCity, setPendingCity] = useState<string | null>(null);

  // Каждое открытие — с шага городов: иначе шторка откроется на районах прошлого выбора.
  useEffect(() => {
    if (props.open) {
      setStep('city');
      setPendingCity(null);
    }
  }, [props.open]);

  const apply = (next: CitySelection): void => {
    props.onApply(next);
    props.onClose();
  };

  const pickCity = (id: string): void => {
    // Город без районов применяется сразу; с районами — ведём на второй шаг.
    if (districtsOf(id).length === 0) {
      apply({ city: id, district: null });
      return;
    }
    setPendingCity(id);
    setStep('district');
  };

  const title =
    step === 'city'
      ? t('geoPickCity')
      : (cityName(pendingCity ?? '', lang) ?? t('geoPickDistrict'));

  return (
    <BottomSheet open={props.open} title={title} onClose={props.onClose}>
      {step === 'city' ? (
        <div>
          <Row
            label={props.clearLabel}
            active={props.value.city === null}
            onClick={() => apply({ city: null, district: null })}
          />
          {UZ_CITIES.map((c) => (
            <Row
              key={c.id}
              label={lang === 'ru' ? c.nameRu : c.nameUz}
              active={props.value.city === c.id}
              hasChildren={districtsOf(c.id).length > 0}
              onClick={() => pickCity(c.id)}
            />
          ))}
        </div>
      ) : (
        <div>
          <button
            type="button"
            onClick={() => setStep('city')}
            className="mb-1 flex items-center gap-1 py-1.5 text-[13px] font-semibold text-accent"
          >
            <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
            {t('back')}
          </button>
          <Row
            label={t('geoAllDistricts')}
            active={props.value.city === pendingCity && props.value.district === null}
            onClick={() => apply({ city: pendingCity, district: null })}
          />
          {districtsOf(pendingCity ?? '').map((d) => (
            <Row
              key={d.id}
              label={lang === 'ru' ? d.nameRu : d.nameUz}
              active={props.value.district === d.id}
              onClick={() => apply({ city: pendingCity, district: d.id })}
            />
          ))}
        </div>
      )}
    </BottomSheet>
  );
}

/** Строка списка: подпись, галочка у выбранного, шеврон у города с районами. */
function Row(props: {
  label: string;
  active: boolean;
  hasChildren?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex w-full items-center justify-between gap-3 border-b border-hair py-3 text-left last:border-b-0"
    >
      <span className={`text-[14px] ${props.active ? 'font-semibold text-accent' : ''}`}>
        {props.label}
      </span>
      {props.active ? (
        <Check size={17} strokeWidth={2.25} className="shrink-0 text-accent" aria-hidden="true" />
      ) : props.hasChildren ? (
        <ChevronRight size={17} strokeWidth={2} className="shrink-0 text-ink-3" aria-hidden="true" />
      ) : null}
    </button>
  );
}
